import {useRef, useState} from 'react';

import {Dropdown} from 'primereact/dropdown';
import {InputNumber} from 'primereact/inputnumber';
import {Checkbox} from 'primereact/checkbox';
import {InputTextarea} from 'primereact/inputtextarea';
import {AutoComplete} from 'primereact/autocomplete';
import {Calendar} from 'primereact/calendar';
import {FormatDisplay} from '../format';
import {DomHandler, ObjectUtils} from 'primereact/utils';

type DataType = ('' | 'date' | 'date-time' | 'number' | 'checkbox' | 'dropdown' | 'autocomplete' | 'custom');

interface IColumn {
  field: string;
  header: string;
  style?: any;
  className?: string;
  dataType?: DataType;

  inputProps?: any;

  customCell?(rowData: any, rowIndex: number): any;

  editable?: boolean;

  checkEditable?(rowData: any): boolean;

  invisible?: boolean;

  addButton?: boolean;

  onAddButtonClick?(rowData: any, column: IColumn): void;

  required?: boolean;
  maxLength?: number;

  rowReorder?: boolean;
}

interface CellEditingProps {
  dataKey?: string;
  columns: Array<IColumn>;
  items: Array<any>;

  onCellChange?(rowData: any, column: IColumn, newValue: any): void;

  onRowReorder?(event: any): void;
}

export const useCellEditing = (props: CellEditingProps) => {

  const rowDragging = useRef(false);
  const draggedRowIndex = useRef(null);
  const droppedRowIndex = useRef(null);

  const {dataKey, columns, items, onCellChange} = props;

  const [selectedItem, setSelectedItem] = useState(null);

  const onMouseDown = column => e => {
    if (column.rowReorder) {
      if (e.target.parentNode.nodeName === 'TR') {
        e.target.parentNode.setAttribute("draggable", "true");
      }
    }
  }

  const onDragEnd = e => {
    rowDragging.current = false;
    draggedRowIndex.current = null;
    droppedRowIndex.current = null;
    e.target.setAttribute("draggable", "false");
  }

  const onDragStart = index => e => {
    rowDragging.current = true;
    draggedRowIndex.current = index;
    e.dataTransfer.setData('text', 'b'); // For firefox
  }

  const onDragOver = index => event => {

    if (rowDragging.current && draggedRowIndex.current !== index) {
      const rowElement = event.currentTarget;
      const rowY = DomHandler.getOffset(rowElement).top + DomHandler.getWindowScrollTop();
      const pageY = event.pageY + window.scrollY;
      const rowMidY = rowY + DomHandler.getOuterHeight(rowElement, false) / 2;
      const prevRowElement = rowElement.previousElementSibling;

      if (pageY < rowMidY) {
        DomHandler.removeClass(rowElement, 'iam-dragpoint-bottom');

        droppedRowIndex.current = index;
        if (prevRowElement) DomHandler.addClass(prevRowElement, 'iam-dragpoint-bottom');
        else DomHandler.addClass(rowElement, 'iam-dragpoint-top');
      } else {
        if (prevRowElement) DomHandler.removeClass(prevRowElement, 'iam-dragpoint-bottom');
        else DomHandler.addClass(rowElement, 'iam-dragpoint-top');

        droppedRowIndex.current = index + 1;
        DomHandler.addClass(rowElement, 'iam-dragpoint-bottom');
      }
    }
    event.preventDefault();
  }

  const onDragLeave = event => {
    const rowElement = event.target;
    const prevRowElement = rowElement.previousElementSibling;

    if (prevRowElement) {
      DomHandler.removeClass(prevRowElement, 'iam-dragpoint-bottom');
    }

    DomHandler.removeClass(rowElement, 'iam-dragpoint-bottom');
    DomHandler.removeClass(rowElement, 'iam-dragpoint-top');
  }

  const onDrop = event => {
    if (droppedRowIndex.current != null) {
      let dropIndex = draggedRowIndex.current > droppedRowIndex.current ? droppedRowIndex.current : droppedRowIndex.current === 0 ? 0 : droppedRowIndex.current - 1;
      let val = [...items];

      ObjectUtils.reorderArray(val, draggedRowIndex.current, dropIndex);

      if (props.onRowReorder) {
        props.onRowReorder({
          originalEvent: event,
          value: val,
          dragIndex: draggedRowIndex.current,
          dropIndex: dropIndex
        });
      }
    }
    onDragLeave(event);
    onDragEnd(event);
    event.preventDefault();
  }

  const getCellTemplate = (rowData, rowIndex) => {
    return columns.map((column, index) => {
      if (column.invisible !== true) {
        const value = rowData[column.field], readOnly = !column.editable;
        const _onCellChange = (newValue) => {
          onCellChange(rowData, column, newValue);
        }

        let body: any, style = {...column.style};
        const {inputProps} = column;

        switch (column.dataType) {
          case 'checkbox':
            body = <Checkbox checked={value} onChange={e => _onCellChange(e.checked)} readOnly={readOnly}/>
            style.textAlign = 'center';
            break;
          case 'number':
            body = <InputNumber locale={FormatDisplay.locales} value={value} {...inputProps} onChange={e => _onCellChange(e.value)} onValueChange={e => _onCellChange(e.value)} readOnly={readOnly}/>
            break;
          case 'date':
            body = <Calendar value={value} {...inputProps} onChange={e => _onCellChange(e.value)} dateFormat="dd/mm/yy" disabled={readOnly}/>
            break;
          case 'date-time':
            body = <Calendar value={value} {...inputProps} onChange={e => _onCellChange(e.value)} dateFormat="dd/mm/yy" showTime disabled={readOnly}/>
            break;
          case 'dropdown':
            if (column.checkEditable === undefined || (column.checkEditable && column.checkEditable(rowData))) {
              body = <Dropdown value={value} {...inputProps} onChange={e => _onCellChange(e.value)} disabled={readOnly}/>
            } else {
              body = value;
            }
            break;
          case 'autocomplete':
            if (column.addButton) {
              body = (
                <div className="p-inputgroup">
                  <AutoComplete value={value} {...inputProps} onChange={e => _onCellChange(e.value)} disabled={readOnly}/>
                  <span className="p-inputgroup-addon">
                      <i className="pi pi-plus" onClick={() => column.onAddButtonClick(rowData, column)}/>
                  </span>
                </div>
              )
            } else {
              body = <AutoComplete value={value} {...inputProps} onChange={e => _onCellChange(e.value)} disabled={readOnly}/>
            }
            break;
          case 'custom':
            body = column.customCell(rowData, rowIndex)
            break;
          default:
            // body = <InputText value={value} {...inputProps} onChange={e => _onCellChange(e.target.value)} readOnly={readOnly}/>
            body = <InputTextarea value={value || ''} {...inputProps} onChange={e => _onCellChange(e.target.value)} rows={1} autoResize readOnly={readOnly} maxLength={column.maxLength}/>
        }
        return (
          <td key={index} role="cell" style={style} className={column.className} onMouseDown={onMouseDown(column)}>
            {body}
          </td>
        )
      } else {
        return null;
      }
    });
  }

  const render = () => {

    return (
      <div className="p-datatable p-component p-datatable-responsive-scroll p-datatable-gridlines p-datatable-sm iam-cell-editing p-fluid">
        <div className="p-datatable-wrapper">
          <table className="p-datatable-table">
            <thead className="p-datatable-thead">
            <tr role="row">
              {
                columns.map(({header, style, className, invisible}, index) => {
                  if (invisible !== true) {
                    return (
                      <th key={index} role="columnheader" style={style} className={className}>
                        <div className="p-column-header-content"><span className="p-column-title">{header}</span></div>
                      </th>
                    )
                  } else {
                    return null;
                  }
                })
              }
            </tr>
            </thead>
            <tbody className="p-datatable-tbody">
            {
              items.map((item, index) => {
                let trClass = 'p-selectable-row';
                if (dataKey && selectedItem && selectedItem[dataKey] && selectedItem[dataKey] === item[dataKey]) {
                  trClass = 'p-highlight p-selectable-row';
                }
                return (
                  <tr key={index} role="row" className={trClass} onClick={() => setSelectedItem(item)}
                      onDragStart={onDragStart(index)} onDragEnd={onDragEnd} onDragOver={onDragOver(index)} onDragLeave={onDragLeave} onDrop={onDrop}>
                    {getCellTemplate(item, index)}
                  </tr>
                )
              })
            }
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return {render, selectedItem, setSelectedItem};
}